import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import LegalLinks from '@/components/LegalLinks';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppContentContainer,
  AppFieldGroup,
  AppInput,
  AppPageIntro,
  AppSectionHeader,
  AppShell,
  AppText,
} from '../../ui-foundation';

export default function VerifyMfaPage() {
  const navigate = useNavigate();
  const { user, signOut, refreshSecurity } = useApp();
  const [code, setCode] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    async function loadFactor() {
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.mfa.listFactors();

      if (error) {
        console.error(error);
        toast.error('Could not load your authenticator setup.');
      } else {
        const verifiedFactor = data.totp.find((factor) => factor.status === 'verified');
        setFactorId(verifiedFactor?.id || null);
      }

      setLoading(false);
    }

    void loadFactor();
  }, [user]);

  const verifyCode = async () => {
    if (!factorId) {
      toast.error('No authenticator app is connected to this account.');
      return;
    }

    const nextCode = code.trim();
    if (!nextCode) {
      toast.error('Enter the 6-digit authenticator code.');
      return;
    }

    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code: nextCode,
    });

    if (error) {
      console.error(error);
      toast.error(error.message || 'That code did not work.');
    } else {
      await refreshSecurity();
      toast.success('Two-step verification complete.');
      navigate('/', { replace: true });
    }

    setVerifying(false);
  };

  return (
    <AppShell className="min-h-screen justify-center px-4 py-6 sm:py-10">
      <AppContentContainer maxWidth={540} className="w-full animate-fade-in">
        <div className="rounded-[32px] border border-border bg-card/95 p-4 shadow-sm sm:p-5">
          <div className="rounded-[28px] border border-primary/10 bg-[linear-gradient(180deg,rgba(107,53,102,0.08),rgba(255,255,255,0.92))] px-5 py-6 sm:px-7 sm:py-7">
            <AppPageIntro
              title="Two-step verification"
              subtitle="Enter the code from your authenticator app to continue into Adulting."
            />
          </div>

          <div className="mt-4">
            <AppCard>
              <AppCardHeader>
                <AppSectionHeader
                  title={
                    !user
                      ? 'Sign in first'
                      : factorId
                        ? 'Authenticator code'
                        : 'Open your authenticator app'
                  }
                  subtitle={
                    !user
                      ? 'Use your Adulting account first, then come back here to finish two-step verification.'
                      : factorId
                        ? 'Enter the latest 6-digit code from your authenticator app.'
                        : loading
                          ? 'Checking your security setup now.'
                          : 'This account does not currently have a verified authenticator app connected.'
                  }
                />
              </AppCardHeader>
              <AppCardBody>
                {!user ? (
                  <div className="space-y-4">
                    <AppText color="$colorMuted" fontSize="$sm">
                      Adulting can only verify a two-step code after your account session is active.
                    </AppText>
                    <AppButton size="sm" onPress={() => navigate('/auth', { replace: true })}>
                      Back to sign in
                    </AppButton>
                  </div>
                ) : !factorId && !loading ? (
                  <div className="space-y-4">
                    <AppText color="$colorMuted" fontSize="$sm">
                      No verified authenticator app is connected to this account yet, so there is no
                      code to check on this page.
                    </AppText>
                    <AppButton size="sm" tone="outline" onPress={() => void signOut()}>
                      Sign out instead
                    </AppButton>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <AppFieldGroup
                      label="Authenticator code"
                      helper="Type the 6-digit code exactly as it appears."
                    >
                      <AppInput
                        value={code}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                        }
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="123456"
                        disabled={loading}
                      />
                    </AppFieldGroup>

                    <AppButton
                      size="sm"
                      onPress={verifyCode}
                      disabled={loading || verifying}
                    >
                      {verifying ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                      {loading ? 'Loading...' : verifying ? 'Checking...' : 'Verify and continue'}
                    </AppButton>

                    <AppButton size="sm" tone="outline" onPress={() => void signOut()}>
                      Sign out instead
                    </AppButton>
                  </div>
                )}
              </AppCardBody>
            </AppCard>

            <div className="mt-4 rounded-[24px] border border-border bg-card/80 px-5 py-4 shadow-sm">
              <LegalLinks />
            </div>
          </div>
        </div>
      </AppContentContainer>
    </AppShell>
  );
}
